// Generated response card with citations and actions.

import { useMemo, useRef, useState } from 'react';
import { marked } from 'marked';
import { Check, ChevronDown, ChevronUp, CircleFadingPlus, ExternalLink, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { sanitizeHtmlWithNewTabLinks } from '@/lib/sanitize';
import { cn } from '@/lib/utils';
import { useResponseSources } from '@/hooks/use-response-sources';
import { CollapseBody } from './collapse-toggle';
import { Astroid } from './icons';
import { sourceAnchorFor, type SourceAnchor } from './source-anchor';
import { nestBulletsUnderNumbers } from './sentinel-workspace.helpers';
import { RESPONSE_ORIGIN, type GeneratedResponse, type ResponseSource } from './types';
import { TraceStepRow } from './composer-bars';

const BODY_PROSE = [
  'max-w-none break-words text-[12px] leading-normal text-text-prose',
  '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
  '[&_p]:my-2',
  '[&_a]:text-blue-500 [&_a]:underline',
  '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
  // A nested list steps in from its parent's text, not from the marker again.
  '[&_li>ul]:my-0 [&_li>ol]:my-0 [&_li>ul]:pl-4 [&_li>ol]:pl-4 [&_li>p]:my-0',
  // A generated reply can carry a code block, image or table. `break-words` cannot wrap those,
  // so without these they widen the card instead of scrolling or scaling inside it.
  '[&_pre]:my-2 [&_pre]:max-w-full [&_pre]:overflow-x-auto',
  '[&_img]:max-w-full [&_img]:h-auto',
  '[&_table]:my-2 [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto',
].join(' ');

/** Renders markdown and swaps [[n]] markers for clickable citation chips. */
export function ResponseBody({
  markdown,
  onCitationClick,
  className,
}: {
  markdown: string;
  onCitationClick?: (sourceIndex: number, chip: HTMLElement) => void;
  className?: string;
}) {
  const html = useMemo(() => {
    // Same nesting as the composer, so a draft reads alike in both.
    const raw = marked(nestBulletsUnderNumbers(markdown), { async: false, breaks: true, gfm: true });
    const withChips = raw.replace(
      /\[\[(\d+)\]\]/g,
      (_, n) =>
        `<button type="button" data-citation="${n}" class="citation-chip inline-flex size-4 cursor-pointer items-center justify-center rounded-full bg-gray-300 align-text-top text-[9px] font-medium text-gray-900 transition-colors hover:bg-gray-500">${n}</button>`,
    );
    return sanitizeHtmlWithNewTabLinks(withChips);
  }, [markdown]);

  return (
    <div
      className={cn(BODY_PROSE, className)}
      onClick={(event) => {
        const chip = (event.target as HTMLElement).closest('[data-citation]');
        if (chip && onCitationClick) {
          onCitationClick(Number(chip.getAttribute('data-citation')) - 1, chip as HTMLElement);
        }
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export function ConfidencePill({ pct }: { pct: number | null }) {
  // An unrecorded score is not a zero score.
  if (pct == null) return null;
  return (
    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[12px] font-medium leading-4 text-emerald-400">
      {pct} %
    </span>
  );
}

/** Sources resolved by name carry no extension; only append one when there is something to append. */
function sourceHeading(source: ResponseSource): string {
  if (!source.fileType || source.title.endsWith(`.${source.fileType}`)) return source.title;
  return `${source.title} .${source.fileType}`;
}

/** Floating source preview anchored above the clicked citation / source chip. */
export function SourcePopover({
  source,
  anchor,
  onClose,
}: {
  source: ResponseSource;
  anchor: SourceAnchor;
  onClose: () => void;
}) {
  return (
    <>
      <button type="button" className="fixed inset-0 z-40 cursor-default" onClick={onClose} aria-label="Close sources" />
      <div
        style={{ left: anchor.x, top: anchor.y }}
        className="absolute z-50 w-105 -translate-x-1/2 -translate-y-[calc(100%+10px)] rounded-3xl bg-white p-5 shadow-[0_12px_32px_rgba(0,0,0,0.16)] border border-gray-500"
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-semibold leading-6 text-text-heading">
            {sourceHeading(source)}
          </p>
          {source.url && (
            <a
              href={source.url}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${source.title}`}
              className="mt-1 shrink-0 text-gray-800 transition-transform hover:scale-105 hover:text-gray-900"
            >
              <ExternalLink className="size-4" />
            </a>
          )}
        </div>
        {/* Uploaded sources carry no summary, so the quote block would otherwise sit there empty. */}
        {source.excerpt && (
          <div className="mt-3 max-h-64 overflow-y-hidden border-l border-gray-500 pl-2.5">
            <p className="text-xs leading-4.5 text-text-quote">{source.excerpt}</p>
          </div>
        )}
      </div>
    </>
  );
}

/** Collapsible chips for the articles and workflows a card drew on. */
export function SourcesRow({
  sources,
  onOpen,
}: {
  sources: ResponseSource[];
  onOpen: (index: number, chip: HTMLElement) => void;
}) {
  const [open, setOpen] = useState(true);
  if (sources.length === 0) return null;

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        className="flex cursor-pointer items-center gap-1 text-[12px] leading-4 text-gray-900 transition-opacity hover:opacity-70"
      >
        Sources ({sources.length})
        <ChevronUp className={cn('size-3 transition-transform duration-300', !open && 'rotate-180')} />
      </button>
      {/* Padding sits inside the clipped box, so it folds away with the chips. */}
      <CollapseBody open={open}>
        <div className="flex flex-wrap gap-1.5 pt-2">
          {sources.map((source, index) => (
            <button
              key={source.id}
              type="button"
              onClick={(event) => onOpen(index, event.currentTarget)}
              className="cursor-pointer rounded-full bg-gray-100 px-3 py-1 text-[12px] leading-4 text-gray-900 transition-colors hover:bg-gray-300"
            >
              {source.title}
            </button>
          ))}
        </div>
      </CollapseBody>
    </div>
  );
}

/** Read-only execution evidence for a response already dispatched by legacy automation. */
export function AutomatedResponseEvidence({
  response,
  defaultOpen = true,
}: {
  response: GeneratedResponse;
  /** Starts folded when the reply itself is shown below it, so the reply is what the agent reads first. */
  defaultOpen?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [openSource, setOpenSource] = useState<SourceAnchor | null>(null);
  const [evidenceOpen, setEvidenceOpen] = useState(defaultOpen);
  const sources = useResponseSources(response);
  if (response.trace.length === 0 && sources.length === 0) return null;

  return (
    <div ref={containerRef} className="relative flex w-full flex-col gap-3 rounded-2xl border border-gray-500 bg-white p-4">
      {openSource && sources[openSource.index] && (
        <SourcePopover source={sources[openSource.index]} anchor={openSource} onClose={() => setOpenSource(null)} />
      )}
      {response.trace.length > 0 && (
        <div className="flex flex-col">
          <button
            type="button"
            onClick={() => setEvidenceOpen((previous) => !previous)}
            aria-expanded={evidenceOpen}
            className="flex cursor-pointer items-center gap-1 self-start font-mono text-[11px] font-medium uppercase leading-4 text-text-disabled transition-opacity hover:opacity-70"
          >
            {response.trace.length} {response.trace.length === 1 ? 'step' : 'steps'} performed
            <ChevronDown className={cn('size-3 text-gray-700 transition-transform duration-300', !evidenceOpen && '-rotate-90')} />
          </button>
          <CollapseBody open={evidenceOpen}>
            <div className="flex flex-col gap-2.5 pt-2.5">
              {response.trace.map((step, index) => (
                <TraceStepRow key={step.id} step={step} showConnector={index < response.trace.length - 1} />
              ))}
            </div>
          </CollapseBody>
        </div>
      )}
      {/* The card's gap folds away with the sources, so a closed card ends at its header. */}
      <CollapseBody open={evidenceOpen} rootClassName={cn('transition-[grid-template-rows,opacity,margin]', !evidenceOpen && '-mt-3')}>
        <SourcesRow
          sources={sources}
          onOpen={(index, chip) => setOpenSource(sourceAnchorFor(containerRef.current, chip, index))}
        />
      </CollapseBody>
    </div>
  );
}

interface ResponseCardProps {
  response: GeneratedResponse;
  /** Hand the ticket back. Omitted where there is no ticket to hand back, which hides the button. */
  onReject?: () => void;
  onRefine: () => void;
  /** Drafts again from current ticket data. Omitted where that is not allowed, which hides the button. */
  onRegenerate?: () => void;
  /** Absent where the response is only read (a follow-up the viewer cannot send), which hides Use Response. */
  onUse?: () => void;
  refineDisabled: boolean;
}

export function ResponseCard({
  response,
  onReject,
  onRefine,
  onRegenerate,
  onUse,
  refineDisabled,
}: ResponseCardProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [openSource, setOpenSource] = useState<SourceAnchor | null>(null);
  // Folded to its header and buttons; tied to this response, so the next draft always opens.
  const [collapsedId, setCollapsedId] = useState<string | null>(null);
  const open = collapsedId !== response.id;
  const sources = useResponseSources(response);
  const isPostAction = response.origin === RESPONSE_ORIGIN.postAction;
  const rejectVisible = onReject !== undefined && !isPostAction;
  // Not after a confirmed action: a fresh run could plan that action again.
  const regenerateVisible = onRegenerate !== undefined && !isPostAction && !refineDisabled;

  return (
    <div
      ref={cardRef}
      className="relative flex max-h-120 min-h-0 w-full flex-col gap-4 rounded-2xl border border-gray-500 bg-white/80 p-4 backdrop-blur-[6px]"
    >
      {openSource !== null && sources[openSource.index] && (
        <SourcePopover source={sources[openSource.index]} anchor={openSource} onClose={() => setOpenSource(null)} />
      )}

      <div className="flex shrink-0 items-center justify-between">
        {/* Same toggle as the generating card: the title row folds the card, its chevron beside the label. */}
        <button
          type="button"
          onClick={() => setCollapsedId(open ? response.id : null)}
          aria-expanded={open}
          className="flex cursor-pointer items-center gap-2.5 transition-opacity hover:opacity-70"
        >
          <Astroid filled className="size-[13px] text-purple-500" />
          <span className="font-mono text-[12px] font-medium uppercase leading-4 text-text-disabled">
            Generated Response
          </span>
          <ChevronDown className={cn('size-[14px] text-gray-700 transition-transform duration-300', !open && '-rotate-90')} />
        </button>
        <ConfidencePill pct={response.confidencePct} />
      </div>

      {/* The card's gap folds away with the body, so a closed card is just its header and buttons. */}
      <CollapseBody
        open={open}
        rootClassName={cn('min-h-0 flex-1 transition-[grid-template-rows,opacity,margin]', !open && '-my-2')}
        className="flex flex-col gap-4"
      >
        <div className="min-h-0 w-full flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <ResponseBody
            markdown={response.markdown}
            onCitationClick={(index, chip) => setOpenSource(sourceAnchorFor(cardRef.current, chip, index))}
          />
        </div>

        <div className="shrink-0">
          <SourcesRow
            sources={sources}
            onOpen={(index, chip) => setOpenSource(sourceAnchorFor(cardRef.current, chip, index))}
          />
        </div>
      </CollapseBody>

      {/* Wraps on narrow screens so the buttons stay inside the card. */}
      <div
        className={cn('flex w-full shrink-0 flex-wrap items-center gap-1.5', rejectVisible ? 'justify-between' : 'justify-end')}
      >
        {rejectVisible && (
          <Button variant="ghost" onClick={onReject} className="text-[13px] font-normal text-gray-900">
            <X className="size-[14px]" />
            Reject &amp; Unassign
          </Button>
        )}
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {regenerateVisible && (
            <Button variant="outline" onClick={onRegenerate} className="text-[13px] font-normal">
              <RefreshCw className="size-[14px]" />
              Regenerate
            </Button>
          )}
          {!refineDisabled && (
            <Button variant="outline" onClick={onRefine} className="text-[13px] font-normal">
              <CircleFadingPlus className="size-[14px]" />
              Refine response
            </Button>
          )}
          {onUse && (
            <Button onClick={onUse} className="text-[13px] font-normal">
              <Check className="size-[14px]" />
              Use Response
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
